import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useChurches } from '@/hooks/useChurches';
import { useRecentChurches } from '@/hooks/useRecentChurches';
import { useUserRole } from '@/hooks/useUserRole';
import { Building2, ChevronDown, Search, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

export function ChurchSwitcher() {
  const navigate = useNavigate();
  const { data: churches = [], isLoading: churchesLoading } = useChurches();
  const { roles, isSuperAdmin } = useUserRole();
  const { recentChurches, addRecentChurch, getRecentChurches } = useRecentChurches();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter churches based on user role and search query
  const filteredChurches = React.useMemo(() => {
    let filtered = churches;

    // If not super admin, filter to only show user's churches
    if (!isSuperAdmin && Array.isArray(roles) && roles.length > 0) {
      const userChurchIds = roles.map(role => role.church_id).filter(Boolean);
      filtered = churches.filter(church => userChurchIds.includes(church.id));
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(church =>
        church.name.toLowerCase().includes(query) ||
        church.email?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [churches, isSuperAdmin, roles, searchQuery]);

  const recentChurchesList = getRecentChurches(churches);

  const handleChurchSelect = (churchId: string) => {
    addRecentChurch(churchId);
    navigate(`/church/${churchId}`);
    setOpen(false);
    setSearchQuery('');
  };

  const getPlanTierBadge = (planTier?: string) => {
    if (!planTier) return null;

    const tierColors = {
      'Free': 'bg-gray-100 text-gray-800',
      'Starter': 'bg-blue-100 text-blue-800',
      'Pro': 'bg-purple-100 text-purple-800',
      'Enterprise': 'bg-gold-100 text-gold-800',
    };

    return (
      <Badge 
        variant="secondary" 
        className={cn(
          'text-xs',
          tierColors[planTier as keyof typeof tierColors] || 'bg-gray-100 text-gray-800'
        )}
      >
        {planTier}
      </Badge>
    );
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + O (church/org switcher). Note: a plain Ctrl/Cmd + /
      // is reserved for the shell's accessibility-help dialog, so this switcher
      // intentionally avoids that chord to prevent both firing on one keypress.
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'o') {
        event.preventDefault();
        setOpen(!open);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (churchesLoading) {
    // Super admins always get the switcher, so a loading placeholder is fine.
    // Other users only see it when they manage multiple churches — which isn't
    // known until the query resolves — so render nothing rather than flashing a
    // placeholder that then disappears for single-church admins.
    if (!isSuperAdmin) return null;
    return (
      <Button variant="outline" size="sm" disabled>
        <Building2 className="h-4 w-4 mr-2" />
        Loading...
        <ChevronDown className="h-4 w-4 ml-2" />
      </Button>
    );
  }

  // If user has no churches or only one church and not super admin, don't show switcher
  if (!isSuperAdmin && filteredChurches.length <= 1) {
    return null;
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="min-w-[200px] justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              {isSuperAdmin ? 'All Churches' : `${filteredChurches.length} Church${filteredChurches.length !== 1 ? 'es' : ''}`}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        className="w-80 p-0" 
        align="start"
        side="bottom"
      >
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search churches..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {/* Recent Churches Section */}
          {recentChurchesList.length > 0 && !searchQuery && (
            <>
              <DropdownMenuLabel className="px-3 py-2 text-xs font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-3 w-3" />
                Recently Viewed
              </DropdownMenuLabel>
              {recentChurchesList.map((church) => (
                <div
                  key={`recent-${church.id}`}
                  className="px-3 py-2 hover:bg-accent cursor-pointer flex items-center gap-3"
                  onClick={() => handleChurchSelect(church.id)}
                >
                  <Avatar className="h-8 w-8">
                    {church.logo_url ? (
                      <img src={church.logo_url} alt={church.name} className="rounded-full" />
                    ) : (
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {church.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{church.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {church.city && church.state ? `${church.city}, ${church.state}` : church.email}
                    </div>
                  </div>
                  {getPlanTierBadge(church.plan_tier)}
                </div>
              ))}
              <DropdownMenuSeparator />
            </>
          )}

          {/* All Churches Section */}
          <DropdownMenuLabel className="px-3 py-2 text-xs font-medium text-muted-foreground">
            {searchQuery ? 'Search Results' : 'All Churches'}
          </DropdownMenuLabel>
          
          {filteredChurches.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              {searchQuery ? 'No churches found matching your search.' : 'No churches available.'}
            </div>
          ) : (
            filteredChurches.map((church) => {
              const isRecent = recentChurches.includes(church.id);
              return (
                <div
                  key={church.id}
                  className={cn(
                    "px-3 py-2 hover:bg-accent cursor-pointer flex items-center gap-3",
                    isRecent && "opacity-60"
                  )}
                  onClick={() => handleChurchSelect(church.id)}
                >
                  <Avatar className="h-8 w-8">
                    {church.logo_url ? (
                      <img src={church.logo_url} alt={church.name} className="rounded-full" />
                    ) : (
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {church.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate flex items-center gap-2">
                      {church.name}
                      {isRecent && (
                        <Clock className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {church.city && church.state ? `${church.city}, ${church.state}` : church.email}
                    </div>
                  </div>
                  {getPlanTierBadge(church.plan_tier)}
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
